import { Close as CloseIcon } from 'components/icons';
import { Key, MouseEventHandler, ReactNode } from 'react';
import { cn } from 'utils/cn';
import { t } from 'utils/getTranslations';

/** What an Autocomplete's `getTagProps` hands each tag. */
export type TagProps = {
    key?: Key;
    className?: string;
    disabled?: boolean;
    onDelete?: MouseEventHandler;
    /**
     * The chip of a record section in the new design, which its read view draws too: the kit's chip, as wide in
     * read as in edit, since its remove button takes no room. The button shows over the chip's end while the
     * pointer is on the chip or the arrow keys have reached it. The list removes nothing, as it leaves the picked
     * options out.
     */
    section?: boolean;
};

type Props = Omit<TagProps, 'key'> & {
    label: ReactNode;
    /** Before the label, e.g. the lock on a value that cannot be removed. */
    icon?: ReactNode;
    /** After the label, e.g. a marker the dropdown option also carries. */
    adornment?: ReactNode;
    /** The full text, shown on hover when the label is cut off. */
    title?: string;
};

// Over the end of a section chip, on the chip's own background, so that showing it moves nothing: while the chip is
// hovered or holds the focus, or is inside the chip the input's arrow keys have reached.
// ponytail: a touch-only screen has no hover, so there a value goes by the keyboard or Clear all alone. Draw the
// button always on a coarse pointer, the read chip reserving its width, if sections turn out to be edited on tablets.
const SECTION_REMOVE_CLASS =
    'bg-muted text-foreground/70 hover:text-foreground absolute inset-y-0 right-0 hidden cursor-pointer items-center rounded-r-sm pr-1 pl-0.5 group-focus-within/tag:flex group-hover/tag:flex in-focus:flex [&>svg]:size-3.5';

/** One selected value inside a multiple Autocomplete's input, with its remove button. */
export default function AutocompleteTag({
    label,
    icon,
    adornment,
    section,
    className,
    disabled,
    onDelete,
    ...tagProps
}: Props) {
    return (
        <span
            {...tagProps}
            className={cn(
                section
                    ? 'group/tag bg-muted text-foreground relative inline-flex h-5.5 max-w-full items-center rounded-sm px-1.5 text-xs font-medium whitespace-nowrap'
                    : 'border-grey-300 bg-grey-100 text-fg inline-flex max-w-full items-center gap-2 rounded-sm border px-1.5 text-sm',
                disabled && 'opacity-40',
                className
            )}
        >
            <span className="inline-flex min-w-0 items-center gap-1">
                {icon}
                <span className="truncate">{label}</span>
                {adornment}
            </span>
            {onDelete && !disabled ? (
                // Out of the tab order, as the Autocomplete removes a tag from its input with Backspace.
                <button
                    type="button"
                    tabIndex={-1}
                    aria-label={t('remove')}
                    onClick={(event) => {
                        // Kept from the Autocomplete root, whose click focuses the input and so opens the list.
                        event.stopPropagation();
                        onDelete(event);
                    }}
                    className={section ? SECTION_REMOVE_CLASS : 'text-fg/80 inline-flex cursor-pointer'}
                >
                    <CloseIcon fontSize="small" />
                </button>
            ) : null}
        </span>
    );
}
